// Pooled spawn-portal VFX: a force-field hex lattice that opens over ~0.2 s,
// holds while the enemy materializes, then collapses over ~0.3 s.
// One instanced draw call for all portals; every timing input derives from
// world.tick, so identical sim runs render identical portals.

import * as THREE from 'three';
import type { Vec2 } from '../types.js';

// Portal aperture expansion time in sim ticks (~0.2 s at the 60 Hz step rate).
// The renderer scales enemies in over the same window, so export it.
export const PORTAL_OPEN_TICKS = 12;
// Collapse time after the enemy materializes (~0.3 s).
const PORTAL_CLOSE_TICKS = 18;
// World-space quad size; the lattice ring sits well inside it.
const PORTAL_SIZE = 3.2;
// Concurrent portal cap. Formations spawn ≤ 9 units and telegraphs last ≤ 1.5 s,
// so 96 covers worst-case overlap; excess portals drop silently (visual only).
const MAX_PORTALS = 96;

interface PortalSlot {
  active: boolean;
  x: number;
  y: number;
  spawnTick: number;
  telegraphUntil: number;
  seed: number;
}

// Deterministic 0..1 hash (same integer-mix style as instances.ts); portal
// spin variation must not use Math.random or replays would diverge visually.
function hash01(n: number): number {
  let h = Math.imul(n | 0, 2654435761);
  h = Math.imul(h ^ (h >>> 15), 2246822519);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

export class SpawnPortals {
  readonly mesh: THREE.InstancedMesh;
  private readonly slots: PortalSlot[] = [];
  private readonly progressAttr: THREE.InstancedBufferAttribute;
  private readonly seedAttr: THREE.InstancedBufferAttribute;
  private readonly material: THREE.ShaderMaterial;
  private readonly dummy = new THREE.Object3D();

  constructor() {
    const geometry = new THREE.PlaneGeometry(1, 1);
    const progress = new Float32Array(MAX_PORTALS);
    const seed = new Float32Array(MAX_PORTALS);
    this.progressAttr = new THREE.InstancedBufferAttribute(progress, 1);
    this.progressAttr.setUsage(THREE.DynamicDrawUsage);
    this.seedAttr = new THREE.InstancedBufferAttribute(seed, 1);
    this.seedAttr.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('aProgress', this.progressAttr);
    geometry.setAttribute('aSeed', this.seedAttr);

    this.material = new THREE.ShaderMaterial({
      uniforms: {
        // Sim seconds (world.tick / 60); keeps the lattice shimmer deterministic.
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(0xb45cff) }
      },
      vertexShader: PORTAL_VERT,
      fragmentShader: PORTAL_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
      blendEquation: THREE.AddEquation
    });

    this.mesh = new THREE.InstancedMesh(geometry, this.material, MAX_PORTALS);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.count = 0;
    // Above the background grid and enemy bodies; additive, so order is cosmetic.
    this.mesh.renderOrder = 4;

    for (let i = 0; i < MAX_PORTALS; i++) {
      this.slots.push({ active: false, x: 0, y: 0, spawnTick: 0, telegraphUntil: 0, seed: 0 });
    }
  }

  /**
   * Open a portal at `pos`. `spawnTick` is the sim tick the spawn event fired
   * on; `telegraphUntil` is when the enemy materializes and the portal starts
   * closing. Both come straight off the spawn event, never from a wall clock.
   */
  open(pos: Vec2, spawnTick: number, telegraphUntil: number): void {
    const slot = this.findSlot();
    if (!slot) return; // pool exhausted: drop the visual, the sim is unaffected
    slot.active = true;
    slot.x = pos.x;
    slot.y = pos.y;
    slot.spawnTick = spawnTick;
    // Never close before the open animation completes.
    slot.telegraphUntil = Math.max(telegraphUntil, spawnTick + PORTAL_OPEN_TICKS);
    slot.seed = hash01(spawnTick * 31 + Math.floor(pos.x * 7) + Math.floor(pos.y * 13));
  }

  /** Recompute envelopes and rewrite the instance buffers for this frame. */
  update(tick: number, alpha: number): void {
    const t = tick + alpha;
    this.material.uniforms.uTime.value = t / 60;
    let count = 0;
    for (const slot of this.slots) {
      if (!slot.active) continue;
      const age = t - slot.spawnTick;
      if (age < 0) {
        // Tick went backwards: the run restarted. Retire the stale portal.
        slot.active = false;
        continue;
      }
      let env: number;
      if (t < slot.telegraphUntil) {
        env = Math.min(1, age / PORTAL_OPEN_TICKS);
      } else {
        const close = (t - slot.telegraphUntil) / PORTAL_CLOSE_TICKS;
        if (close >= 1) {
          slot.active = false;
          continue;
        }
        env = 1 - close;
      }
      // Smooth both ends of the envelope so the aperture eases in and out.
      env = env * env * (3 - 2 * env);

      this.dummy.position.set(slot.x, slot.y, 0.03);
      this.dummy.rotation.z = 0;
      this.dummy.scale.setScalar(PORTAL_SIZE);
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(count, this.dummy.matrix);
      this.progressAttr.setX(count, env);
      this.seedAttr.setX(count, slot.seed);
      count++;
    }
    this.mesh.count = count;
    if (count > 0) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.progressAttr.needsUpdate = true;
      this.seedAttr.needsUpdate = true;
    }
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }

  private findSlot(): PortalSlot | undefined {
    for (const slot of this.slots) {
      if (!slot.active) return slot;
    }
    // Full: steal the slot nearest to closing so the newest spawn stays readable.
    let best: PortalSlot | undefined;
    let bestRemaining = Infinity;
    for (const slot of this.slots) {
      const remaining = slot.telegraphUntil - slot.spawnTick;
      if (remaining < bestRemaining) {
        bestRemaining = remaining;
        best = slot;
      }
    }
    return best;
  }
}

const PORTAL_VERT = /* glsl */ `
attribute float aProgress;
attribute float aSeed;
varying vec2 vUv;
varying float vProgress;
varying float vSeed;

void main() {
  vUv = uv;
  vProgress = aProgress;
  vSeed = aSeed;
  vec4 pos = vec4(position, 1.0);
  #ifdef USE_INSTANCING
    pos = instanceMatrix * pos;
  #endif
  gl_Position = projectionMatrix * modelViewMatrix * pos;
}
`;

const PORTAL_FRAG = /* glsl */ `
precision highp float;

uniform float uTime;
uniform vec3 uColor;
varying vec2 vUv;
varying float vProgress;
varying float vSeed;

float hexDist(vec2 p) {
  p = abs(p);
  return max(dot(p, vec2(0.5, 0.8660254)), p.x);
}

void main() {
  float env = vProgress;
  if (env <= 0.004) discard;

  // -1..1 across the quad, slowly spinning; spin phase is per-portal.
  vec2 p = vUv * 2.0 - 1.0;
  float ang = vSeed * 6.2831853 + uTime * 0.7;
  float c = cos(ang);
  float s = sin(ang);
  p = mat2(c, -s, s, c) * p;

  float hd = hexDist(p);
  float rad = length(p);

  // Aperture: the lattice ring expands as the portal opens.
  float R = 0.18 + 0.55 * env;

  // Main hex ring.
  float ring = 1.0 - smoothstep(0.0, 0.05, abs(hd - R));

  // Six spokes from hub to ring, fading inward.
  float th = atan(p.y, p.x);
  float spokeLine = abs(sin(th * 3.0)) * max(hd, 0.08);
  float spokes = (1.0 - smoothstep(0.0, 0.035, spokeLine))
    * (1.0 - smoothstep(R * 0.55, R, hd))
    * step(0.1, hd);

  // Interior concentric hex lattice drifting outward — the force-field fill.
  float f = fract(hd * 6.0 - uTime * 0.9);
  float lat = (1.0 - smoothstep(0.0, 0.09, min(f, 1.0 - f))) * step(hd, R);

  // Soft energy hub at the aperture center.
  float glow = exp(-9.0 * rad * rad) * 0.7;

  float b = ring * 1.1 + spokes * 0.7 + lat * 0.22 + glow;
  b *= env;
  // Soft radial clip so the quad edge never shows.
  b *= 1.0 - smoothstep(0.85, 1.0, rad);
  if (b <= 0.004) discard;
  gl_FragColor = vec4(uColor * b, 1.0);
}
`;
